/**
 * ApiAccessButtons — the model page's "use this model from code" header menu.
 *
 * Owns the shared key state and wires three dialogs together:
 *  - CreateApiKeyDialog (shared with Settings)
 *  - ApiKeyRevealDialog (one-time secret, header-menu flow only)
 *  - SampleRequestDialog (curl snippet; embeds the key as its own reveal)
 *
 * The raw secret is returned exactly once by the API, so it lives only in
 * this component's state — navigating away discards it.
 */

import { useState } from 'react'
import { ChevronDown, Code, KeyRound } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ApiKeyRevealDialog } from '@/components/settings/api-keys/ApiKeyRevealDialog'
import { CreateApiKeyDialog } from '@/components/settings/api-keys/CreateApiKeyDialog'
import { SampleRequestDialog } from '@/components/models/SampleRequestDialog'
import { useTeam } from '@/contexts/TeamContext'
import type { ApiKeyCreateResponse } from '@/hooks/useApiKeys'
import type { Model } from '@/hooks/useModels'

interface ApiAccessButtonsProps {
  model: Model
}

export function ApiAccessButtons({ model }: ApiAccessButtonsProps): React.JSX.Element {
  const { isRestricted } = useTeam()
  const [createOpen, setCreateOpen] = useState(false)
  const [sampleOpen, setSampleOpen] = useState(false)
  // Where the create dialog was launched from. From the sample dialog we
  // swap dialogs (never stack) and return there afterwards — the embedded
  // key IS the reveal. From the header menu we show the one-time reveal.
  const [createOrigin, setCreateOrigin] = useState<'header' | 'sample'>('header')
  // The just-minted key, secret included — exists only while on this page.
  const [createdKey, setCreatedKey] = useState<ApiKeyCreateResponse | null>(null)
  const [revealOpen, setRevealOpen] = useState(false)
  const [useKey, setUseKey] = useState(false)
  const [stream, setStream] = useState(false)

  function handleCreated(key: ApiKeyCreateResponse): void {
    setCreatedKey(key)
    setUseKey(true)
    if (createOrigin === 'header') setRevealOpen(true)
  }

  function handleCreateOpenChange(open: boolean): void {
    setCreateOpen(open)
    // Round-trip back to the sample dialog (create or cancel alike).
    if (!open && createOrigin === 'sample') setSampleOpen(true)
  }

  function launchCreateFromSample(): void {
    setCreateOrigin('sample')
    setSampleOpen(false)
    setCreateOpen(true)
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            <Code className="mr-2 h-4 w-4" />
            API
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* No key minting on restricted demo teams (TRA-832) — the sample
              request stays, with a placeholder key instead of create-and-fill. */}
          {!isRestricted && (
            <DropdownMenuItem
              onSelect={() => { setCreateOrigin('header'); setCreateOpen(true) }}
            >
              <KeyRound className="mr-2 h-4 w-4" />
              Create API key
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => { setSampleOpen(true) }}>
            <Code className="mr-2 h-4 w-4" />
            Sample request
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateApiKeyDialog
        open={createOpen}
        onOpenChange={handleCreateOpenChange}
        onCreated={handleCreated}
      />

      <ApiKeyRevealDialog
        open={revealOpen}
        onOpenChange={setRevealOpen}
        apiKey={createdKey}
        description="Copy this key now. It won't be shown again. While you stay on this page, “Sample request” can embed it for you."
      />

      <SampleRequestDialog
        model={model}
        open={sampleOpen}
        onOpenChange={setSampleOpen}
        createdKey={createdKey}
        useKey={useKey}
        onUseKeyChange={setUseKey}
        stream={stream}
        onStreamChange={setStream}
        onCreateKey={launchCreateFromSample}
      />
    </>
  )
}
